import { getCurrencyFormatter } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { getReclaimPartnerCodeIntakeState } from "@redotech/redo-dispute-common/reclaim-partner-code-intake";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoElevatedOnboarding } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding";
import { RedoElevatedOnboardingHeroPanel } from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding-hero-panel";
import {
  RedoElevatedOnboardingStepSubtitle,
  RedoElevatedOnboardingStepTitle,
} from "@redotech/redo-web/arbiter-components/elevated-onboarding/redo-elevated-onboarding-step-text";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { OnboardingHeroContent } from "../../../onboarding/onboarding-hero-content";
import { useReclaimAccessDefaults } from "../../../payments/chargebacks/use-reclaim-access-defaults";
import { useSetupGuide } from "../../provider/use-setup-guide";
import { getProductConfig } from "../../registry/product-registry";
import type {
  CustomIntakeExperienceChrome,
  IntakeAdditionalStepConfig,
  IntakeAdditionalStepFooterChrome,
  IntakeAnswers,
  IntakeAuthorizationStepConfig,
  IntakeInfoStepConfig,
  IntakeQuestion,
  Product,
} from "../../registry/types";
import { DecryptedText } from "./decrypted-text";
import * as css from "./intake-modal.module.css";
import { IntakeOption } from "./intake-option";
import {
  buildIntakeSteps,
  canUseRequestedIntakeStep,
  getIntakeProgressPercentage,
  getNextIntakeStepId,
  isIntakeContinueDisabled,
  resolveInitialIntakeStepId,
  SETUP_STEP_PARAM,
} from "./intake-steps";
import { allQuestionsAnswered, isAnswered } from "./intake-validation";
import { IntakeStepTransition } from "./step-transition";

export interface IntakeModalProps {
  product: Product;
  /** Submit handler — provider should pipe to submitMerchantSetupIntake RPC. */
  onSubmit: (answers: IntakeAnswers) => Promise<void>;
  onClose?: () => void;
  /** Uses the Concierge-style slide/fade transition between intake steps. */
  animateStepTransitions?: boolean;
}

// Custom intake hero illustrating the merchant onboarding moment.
// Same S3 bucket the rest of the onboarding-v2 assets live in.
const DEFAULT_HERO_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/merchant-onboarding-in-app.webp";

const EMPTY_QUESTIONS: IntakeQuestion[] = [];
const EMPTY_ADDITIONAL_STEPS: IntakeAdditionalStepConfig[] = [];
const IS_LOCAL_DEVELOPMENT = process.env.NODE_ENV === "development";

type ResolvedIntakeInfoStepConfig = IntakeInfoStepConfig & {
  titleAccessory?: ReactNode;
};

/**
 * Multi-step intake modal:
 *   • "welcome": "Welcome to Reclaim" + last-12-months chargeback loss stat
 *   • One step per intake question, prompt rendered as the H1 with no
 *     subtitle and the option list filling the body.
 *   • Optional final information and authorization steps before submit.
 *
 * Button text follows the position: "Continue" on welcome, "Next" between
 * questions/info steps, authorization CTA on authorization, "Get started" on
 * submit (or "Try again" after a submit error).
 *
 * Non-dismissible (intake submit failure must not strand the user — show
 * inline error + retry, never close on escape/backdrop).
 */
export const IntakeModal = memo(function IntakeModal({
  product,
  onSubmit,
  onClose,
  animateStepTransitions = false,
}: IntakeModalProps) {
  const config = getProductConfig(product);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const setupGuide = useSetupGuide();
  const team = useContext(TeamContext);
  const location = useLocation();

  // Answers live on the provider so partial drafts survive the modal
  // unmounting + remounting when the merchant navigates away from the
  // product's routes (the host scopes modal rendering by route).
  const answers = setupGuide.intakeAnswers;
  const setAnswer = setupGuide.setIntakeAnswer;

  const [submitting, setSubmitting] = useState(false);
  const [authorizationPending, setAuthorizationPending] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [customChrome, setCustomChrome] =
    useState<CustomIntakeExperienceChrome | null>(null);
  const [additionalStepFooterChrome, setAdditionalStepFooterChrome] = useState<{
    chrome: IntakeAdditionalStepFooterChrome;
    stepId: string;
  } | null>(null);

  // Pull last-12-months dispute stats (same source the legacy insights
  // page uses) so we can show "you're losing $X to chargebacks every year".
  const statsLoad = useLoad(
    async (signal) => {
      if (!config?.intake.welcome.useDisputeStatsSubtitle) {
        return undefined;
      }
      try {
        const today = Temporal.Now.plainDateISO();
        const result = await merchantRpcClient.getDisputeDashboardStats({
          startDate: today.subtract({ months: 12 }),
          endDate: today,
        });
        if (signal.aborted) {
          return undefined;
        }
        return result;
      } catch {
        return undefined;
      }
    },
    [config?.intake.welcome.useDisputeStatsSubtitle, merchantRpcClient],
  );
  const stats = statsLoad.value;
  const { terms, termsCopy } = useReclaimAccessDefaults({
    enabled: product === "disputes",
    // Must match the surface the submit sends, or this modal quotes one
    // channel's terms and materializes another's: the insights CTA would show
    // free copy, hide the partner-code step, and then bill.
    surface: setupGuide.activationSurface ?? undefined,
  });
  const partnerCodeState = getReclaimPartnerCodeIntakeState(answers);
  // Free-terms teams may have been attributed to a partner via a referral URL
  // (no typed code); fetch that partner so we can co-brand the same way.
  const partnerAttributionLoad = useLoad(
    async () =>
      product === "disputes" && terms?.accessMode === "free"
        ? await merchantRpcClient.getTeamPartnerAttribution({})
        : null,
    [product, terms?.accessMode, merchantRpcClient],
  );
  const partnerAttribution = partnerAttributionLoad.value ?? null;
  const displayedTermsCopy = useMemo<ResolvedIntakeInfoStepConfig>(() => {
    if (product !== "disputes") {
      return termsCopy;
    }
    const partner =
      partnerCodeState.status === "applied"
        ? {
            name: partnerCodeState.partnerName ?? "your partner",
            logo: partnerCodeState.partnerProfileImageUrl ?? null,
          }
        : terms?.accessMode === "free" && partnerAttribution
          ? {
              name: partnerAttribution.partnerName,
              logo: partnerAttribution.logoUrl,
            }
          : null;
    if (!partner) {
      return termsCopy;
    }
    return {
      title: `Welcome from ${partner.name}`,
      titleAccessory: partner.logo ? (
        <Avatar
          alt={`${partner.name} logo`}
          imageUrl={partner.logo}
          name={partner.name}
          size="regular"
        />
      ) : undefined,
      body: "Your partner access is applied, so you can finish Reclaim setup without adding billing. Reclaim will defend your disputes automatically.",
    };
  }, [
    product,
    partnerCodeState.status,
    partnerCodeState.partnerName,
    partnerCodeState.partnerProfileImageUrl,
    terms?.accessMode,
    partnerAttribution,
    termsCopy,
  ]);
  const currency = stats?.currency ?? "USD";
  const formatter = useMemo(() => getCurrencyFormatter(currency), [currency]);
  const annualLoss =
    stats && stats.totalCount > 0
      ? Math.max(0, stats.finalizedAmount - stats.wonAmount)
      : null;

  const questions = config?.intake.questions ?? EMPTY_QUESTIONS;
  const additionalStepContext = useMemo(
    () => ({ answers, reclaimTerms: terms, team }),
    [answers, team, terms],
  );
  const configuredAdditionalSteps =
    config?.intake.additionalSteps ?? EMPTY_ADDITIONAL_STEPS;
  const additionalSteps = useMemo(
    () =>
      configuredAdditionalSteps.filter((step) =>
        step.shouldInclude(additionalStepContext),
      ),
    [additionalStepContext, configuredAdditionalSteps],
  );
  const skippedAdditionalAnswers = useMemo(
    () =>
      configuredAdditionalSteps.reduce<IntakeAnswers>((nextAnswers, step) => {
        if (step.shouldInclude(additionalStepContext)) {
          return nextAnswers;
        }
        return {
          ...nextAnswers,
          ...step.getSkippedAnswers?.(additionalStepContext),
        };
      }, {}),
    [additionalStepContext, configuredAdditionalSteps],
  );
  const finalInfoStep = useMemo(() => {
    if (product !== "disputes") {
      return config?.intake.finalInfoStep ?? null;
    }
    return displayedTermsCopy;
  }, [config?.intake.finalInfoStep, displayedTermsCopy, product]);
  const authorizationStep = config?.intake.authorizationStep;
  const requestedSetupStepId = useMemo(
    () => new URLSearchParams(location.search).get(SETUP_STEP_PARAM),
    [location.search],
  );
  const steps = useMemo(
    () =>
      buildIntakeSteps({
        additionalSteps,
        authorizationStep,
        finalInfoStep,
        isLocalDevelopment: IS_LOCAL_DEVELOPMENT,
        questions,
        team,
      }),
    [additionalSteps, authorizationStep, finalInfoStep, questions, team],
  );
  const effectiveRequestedSetupStepId = useMemo(
    () =>
      canUseRequestedIntakeStep({
        additionalSteps,
        answers,
        authorizationStepId: authorizationStep?.id,
        questions,
        requestedStepId: requestedSetupStepId,
        steps,
      })
        ? requestedSetupStepId
        : null,
    [
      additionalSteps,
      answers,
      authorizationStep?.id,
      questions,
      requestedSetupStepId,
      steps,
    ],
  );
  const appliedRequestedStepRef = useRef<string | null>(
    effectiveRequestedSetupStepId,
  );
  const [currentStepId, setCurrentStepId] = useState(() =>
    resolveInitialIntakeStepId({
      authorizationStepId: authorizationStep?.id,
      requestedStepId: effectiveRequestedSetupStepId,
      steps,
    }),
  );
  useEffect(() => {
    const hasCurrentStep = steps.some((step) => step.id === currentStepId);
    const requestedStepAlreadyApplied =
      !effectiveRequestedSetupStepId ||
      appliedRequestedStepRef.current === effectiveRequestedSetupStepId;
    if (
      hasCurrentStep &&
      requestedStepAlreadyApplied &&
      effectiveRequestedSetupStepId !== authorizationStep?.id
    ) {
      return;
    }
    const nextStepId = resolveInitialIntakeStepId({
      authorizationStepId: authorizationStep?.id,
      requestedStepId: effectiveRequestedSetupStepId,
      steps,
    });
    if (effectiveRequestedSetupStepId) {
      appliedRequestedStepRef.current = effectiveRequestedSetupStepId;
    }
    if (nextStepId !== currentStepId) {
      setCurrentStepId(nextStepId);
    }
  }, [
    authorizationStep?.id,
    currentStepId,
    effectiveRequestedSetupStepId,
    steps,
  ]);
  const currentStep =
    steps.find((step) => step.id === currentStepId) ?? steps[0];
  const lastRecordedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentStep || lastRecordedStepRef.current === currentStep.id) {
      return;
    }
    lastRecordedStepRef.current = currentStep.id;
    setupGuide.recordIntakeStep(product, currentStep.id);
  }, [currentStep, product, setupGuide]);
  const allAnswered = !!config && allQuestionsAnswered(questions, answers);
  const CustomExperience = config?.intake.renderExperience;

  const currentQuestion =
    currentStep?.type === "question"
      ? questions[currentStep.questionIndex]
      : undefined;
  const currentAnswered =
    !!currentQuestion &&
    isAnswered(currentQuestion, answers[currentQuestion.id]);
  const nextStepId = currentStep
    ? getNextIntakeStepId(steps, currentStep.id)
    : null;
  const isLastStep = nextStepId == null;
  const currentAdditionalStep =
    currentStep?.type === "additional"
      ? additionalSteps[currentStep.additionalStepIndex]
      : undefined;
  const currentAdditionalStepComplete =
    currentStep?.type === "additional" &&
    currentAdditionalStep?.isComplete?.(answers) === true;
  const CurrentAdditionalStep = currentAdditionalStep?.render;
  const currentAdditionalStepFooterChrome =
    currentStep?.type === "additional" &&
    additionalStepFooterChrome?.stepId === currentStep.id
      ? additionalStepFooterChrome.chrome
      : null;
  const authorizationComplete =
    !!team && authorizationStep?.completionPredicate(team) === true;
  const showReturnedMissingPermissions =
    currentStep?.type === "authorization" &&
    requestedSetupStepId === currentStep.authorizationStepId &&
    !authorizationComplete;
  const displayedAuthorizationError =
    authorizationError ??
    (showReturnedMissingPermissions && authorizationStep
      ? authorizationStep.errorCopy.missingAfterReturn
      : null);
  const canSkipAuthorizationInDevelopment =
    IS_LOCAL_DEVELOPMENT &&
    currentStep?.type === "authorization" &&
    !!authorizationStep?.developmentSkipCtaText;

  const submitAnswers = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ ...skippedAdditionalAnswers, ...answers });
      // Provider's onSuccess refetches; intakeCompletedAt flips and the
      // modal naturally unmounts via the showIntake gate.
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your answers. Please try again.",
      );
      setSubmitting(false);
    }
  }, [answers, onSubmit, skippedAdditionalAnswers]);

  const handleAdditionalStepContinue = useCallback(() => {
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      void submitAnswers();
    }
  }, [allAnswered, nextStepId, submitAnswers]);

  const handleAdditionalStepFooterChromeChange = useCallback(
    (chrome: IntakeAdditionalStepFooterChrome | null) => {
      const stepId = currentStep?.type === "additional" ? currentStep.id : null;
      setAdditionalStepFooterChrome(
        chrome && stepId ? { chrome, stepId } : null,
      );
    },
    [currentStep?.id, currentStep?.type],
  );

  const currentAdditionalStepProps = useMemo(
    () =>
      currentAdditionalStep
        ? {
            answers,
            onFooterChromeChange: handleAdditionalStepFooterChromeChange,
            onContinue: handleAdditionalStepContinue,
            rpcClient: merchantRpcClient,
            setAnswer,
            team,
          }
        : null,
    [
      answers,
      currentAdditionalStep,
      handleAdditionalStepFooterChromeChange,
      handleAdditionalStepContinue,
      merchantRpcClient,
      setAnswer,
      team,
    ],
  );

  const handleContinue = useCallback(async () => {
    if (!currentStep) {
      return;
    }
    if (currentStep.type === "additional") {
      if (currentAdditionalStepFooterChrome?.onContinue) {
        await currentAdditionalStepFooterChrome.onContinue();
        return;
      }
      if (!currentAdditionalStepComplete) {
        return;
      }
      handleAdditionalStepContinue();
      return;
    }
    if (currentStep.type === "authorization") {
      if (!team || !authorizationStep) {
        setAuthorizationError(
          authorizationStep?.errorCopy.urlError ??
            "Could not open authorization. Please try again.",
        );
        return;
      }
      setAuthorizationPending(true);
      setAuthorizationError(null);
      try {
        const authorizationUrl = await authorizationStep.getAuthorizationUrl({
          rpcClient: merchantRpcClient,
          team,
        });
        window.location.href = authorizationUrl;
      } catch {
        setAuthorizationError(authorizationStep.errorCopy.urlError);
        setAuthorizationPending(false);
      }
      return;
    }
    if (currentStep.type === "question" && !currentAnswered) {
      return;
    }
    if (currentStep.type === "finalInfo" && !allAnswered) {
      return;
    }
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      await submitAnswers();
    }
  }, [
    allAnswered,
    authorizationStep,
    currentAdditionalStepComplete,
    currentAdditionalStepFooterChrome,
    currentAnswered,
    currentStep,
    handleAdditionalStepContinue,
    merchantRpcClient,
    nextStepId,
    submitAnswers,
    team,
  ]);

  const handleSkipAuthorization = useCallback(async () => {
    if (!canSkipAuthorizationInDevelopment) {
      return;
    }
    setAuthorizationError(null);
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      return;
    }
    if (allAnswered) {
      await submitAnswers();
    }
  }, [
    allAnswered,
    canSkipAuthorizationInDevelopment,
    nextStepId,
    submitAnswers,
  ]);

  if (!config) {
    return null;
  }

  const defaultContinueDisabled = isIntakeContinueDisabled({
    allAnswered,
    authorizationPending,
    currentAdditionalStepComplete,
    currentAnswered,
    currentStep,
    submitting,
  });
  const continueDisabled =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continueDisabled != null
      ? currentAdditionalStepFooterChrome.continueDisabled
      : defaultContinueDisabled;
  const additionalStepContinuePending =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continuePending === true;
  const continueText =
    currentStep?.type === "additional" &&
    currentAdditionalStepFooterChrome?.continueText
      ? currentAdditionalStepFooterChrome.continueText
      : currentStep?.type === "welcome"
        ? "Continue"
        : currentStep?.type === "additional"
          ? "Continue"
          : currentStep?.type === "authorization" && authorizationStep
            ? authorizationStep.ctaText
            : isLastStep
              ? error
                ? "Try again"
                : "Get started"
              : "Next";
  const showExternalLinkIcon = currentStep?.type === "authorization";
  const dismissible = config.intake.dismissible === true;
  const heroImageUrl = config.intake.heroImageUrl ?? DEFAULT_HERO_IMAGE_URL;
  const showProgress = config.intake.showProgress ?? !CustomExperience;
  const progressPercent =
    showProgress && CustomExperience && customChrome
      ? customChrome.totalSteps > 0
        ? Math.round(
            ((customChrome.currentStepIndex + 1) / customChrome.totalSteps) *
              100,
          )
        : 0
      : showProgress
        ? getIntakeProgressPercentage(steps, currentStep?.id)
        : undefined;

  const footer = CustomExperience ? (
    customChrome ? (
      <Flex align="center" gap="md" justify="space-between" w="full">
        {customChrome.footerError ? (
          <Text fontSize="sm" textColor="error">
            {customChrome.footerError}
          </Text>
        ) : (
          <div />
        )}
        <Flex align="center" gap="sm">
          {customChrome.onBack ? (
            <RedoButton
              hierarchy="secondary"
              onClick={customChrome.onBack}
              size="lg"
              text={customChrome.backText ?? "Back"}
            />
          ) : null}
          <RedoButton
            disabled={customChrome.continueDisabled}
            hierarchy="primary"
            onClick={() => {
              void customChrome.onContinue();
            }}
            pending={customChrome.continuePending}
            size="lg"
            text={customChrome.continueText}
          />
        </Flex>
      </Flex>
    ) : null
  ) : currentAdditionalStep &&
    !currentAdditionalStep.secondaryFooterButton &&
    !currentAdditionalStepComplete &&
    !currentAdditionalStepFooterChrome ? null : (
    <>
      {currentStep?.type === "additional" &&
        currentAdditionalStep?.secondaryFooterButton &&
        !currentAdditionalStepComplete &&
        currentAdditionalStepProps && (
          <RedoButton
            disabled={
              submitting ||
              authorizationPending ||
              additionalStepContinuePending
            }
            hierarchy="secondary"
            onClick={() => {
              void currentAdditionalStep.secondaryFooterButton?.onClick(
                currentAdditionalStepProps,
              );
            }}
            size="lg"
            text={currentAdditionalStep.secondaryFooterButton.text}
          />
        )}
      {canSkipAuthorizationInDevelopment && authorizationStep && (
        <RedoButton
          disabled={submitting || authorizationPending}
          hierarchy="secondary"
          onClick={handleSkipAuthorization}
          size="lg"
          text={authorizationStep.developmentSkipCtaText}
        />
      )}
      <RedoButton
        disabled={continueDisabled}
        hierarchy="primary"
        onClick={() => {
          void handleContinue();
        }}
        pending={
          currentStep?.type === "additional"
            ? additionalStepContinuePending
            : submitting || authorizationPending
        }
        size="lg"
        text={continueText}
        {...(showExternalLinkIcon ? { IconLeading: LinkExternalIcon } : {})}
      />
    </>
  );

  return (
    <RedoElevatedOnboarding
      bodyScroll={!CustomExperience}
      dismissible={dismissible}
      footer={footer}
      onCloseRequested={onClose}
      productName={config.displayName}
      progressPercent={progressPercent}
      rightPanel={
        <RedoElevatedOnboardingHeroPanel imageUrl={heroImageUrl}>
          <OnboardingHeroContent />
        </RedoElevatedOnboardingHeroPanel>
      }
    >
      {CustomExperience ? (
        <CustomExperience
          onChromeChange={setCustomChrome}
          onClose={dismissible ? onClose : undefined}
          onSubmit={onSubmit}
          product={product}
        />
      ) : (
        <div className={css.stepInner}>
          <IntakeStepTransition
            enabled={animateStepTransitions}
            stepKey={currentStep?.id ?? "missing"}
          >
            {currentStep?.type === "welcome" ? (
              <div className={css.welcomeBlock}>
                <WelcomeContent
                  annualLoss={annualLoss}
                  formatter={formatter}
                  loading={statsLoad.pending}
                  subtitle={config.intake.welcome.subtitle}
                  title={config.intake.welcome.title}
                  useDisputeStatsSubtitle={
                    config.intake.welcome.useDisputeStatsSubtitle === true
                  }
                />
              </div>
            ) : CurrentAdditionalStep && currentAdditionalStepProps ? (
              <CurrentAdditionalStep {...currentAdditionalStepProps} />
            ) : currentStep?.type === "finalInfo" && finalInfoStep ? (
              <FinalInfoContent error={error} step={finalInfoStep} />
            ) : currentStep?.type === "authorization" && authorizationStep ? (
              <AuthorizationContent
                error={displayedAuthorizationError}
                step={authorizationStep}
              />
            ) : currentQuestion ? (
              <div className={css.questionsBlock}>
                <RedoElevatedOnboardingStepTitle noWrap>
                  {currentQuestion.prompt}
                </RedoElevatedOnboardingStepTitle>
                <div
                  className={
                    currentQuestion.layout === "grid"
                      ? css.optionGrid
                      : css.optionList
                  }
                >
                  {currentQuestion.options.map((option) => (
                    <IntakeOption
                      disabled={submitting}
                      isSelected={answers[currentQuestion.id] === option.value}
                      key={option.value}
                      layout={currentQuestion.layout ?? "list"}
                      onClick={() =>
                        setAnswer(currentQuestion.id, option.value)
                      }
                      option={option}
                    />
                  ))}
                  {error && isLastStep && (
                    <Text fontSize="sm" textColor="error">
                      {error}
                    </Text>
                  )}
                </div>
              </div>
            ) : null}
          </IntakeStepTransition>
        </div>
      )}
    </RedoElevatedOnboarding>
  );
});

const WelcomeContent = memo(function WelcomeContent({
  annualLoss,
  formatter,
  loading,
  subtitle,
  title,
  useDisputeStatsSubtitle,
}: {
  annualLoss: number | null;
  formatter: Intl.NumberFormat;
  loading: boolean;
  subtitle: string;
  title: string;
  useDisputeStatsSubtitle: boolean;
}) {
  if (loading) {
    return null;
  }
  const resolvedSubtitle =
    useDisputeStatsSubtitle && annualLoss != null && annualLoss > 0
      ? `Looks like you're currently losing ${formatter.format(annualLoss)} to chargebacks every year. Let's fix that.`
      : subtitle;
  return (
    <>
      <RedoElevatedOnboardingStepTitle noWrap>
        <DecryptedText animateOn="view" sequential speed={40} text={title} />
      </RedoElevatedOnboardingStepTitle>
      <RedoElevatedOnboardingStepSubtitle>
        {resolvedSubtitle}
      </RedoElevatedOnboardingStepSubtitle>
    </>
  );
});

const FinalInfoContent = memo(function FinalInfoContent({
  error,
  step,
}: {
  error: string | null;
  step: ResolvedIntakeInfoStepConfig;
}) {
  return (
    <div className={css.questionsBlock}>
      <RedoElevatedOnboardingStepTitle accessory={step.titleAccessory} noWrap>
        {step.title}
      </RedoElevatedOnboardingStepTitle>
      <Text fontSize="md" mt="lg" textColor="secondary">
        {step.body}
      </Text>
      {error && (
        <Text fontSize="sm" mt="lg" textColor="error">
          {error}
        </Text>
      )}
    </div>
  );
});

const AuthorizationContent = memo(function AuthorizationContent({
  error,
  step,
}: {
  error: string | null;
  step: IntakeAuthorizationStepConfig;
}) {
  return (
    <div className={css.questionsBlock}>
      <RedoElevatedOnboardingStepTitle noWrap>
        {step.title}
      </RedoElevatedOnboardingStepTitle>
      <Text fontSize="md" mt="lg" textColor="secondary">
        {step.body}
      </Text>
      {error && (
        <Text fontSize="sm" mt="lg" textColor="error">
          {error}
        </Text>
      )}
    </div>
  );
});
